/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import React, { useState, useEffect } from "react";

import logo from "../../assets/logo512.png";

// We strive to ensure the user never sees this page, but if the end up here it
// better be fun that one time, and we better fix it before it happens again.
const loadingMessages = [
  "Charging up the electrons...",
  "Untangling power lines...",
  "Calming angry squirrels on transformers...",
  "Synchronizing lightning detectors...",
  "Calibrating wind speed to 'hold onto your hat'...",
  "Upgrading coffee makers to handle storm-level caffeine needs...",
  "Convincing clouds to use their indoor voices...",
  "Negotiating peace treaties between competing weather patterns...",
  "Upgrading linemen's lunch boxes with quantum sandwich storage...",
  "Implementing cutting-edge bird deterrent disco balls...",
  "Preparing TPS Reports...",
  "Negotiating with the aliens to help quickly rebuild power lines...",
  "Attaching rope swings to bucket trucks...",
  "Teaching storm clouds anger management techniques...",
  "Negotiating with gravity for gentler equipment drops...",
  "Instructing electrons on proper social distancing...",
  "Preparing pep talks for sleepy backup generators...",
  "Scheduling group therapy for overworked circuit breakers...",
  "Knitting cozies for cold transformers...",
  "Training carrier pigeons for off-grid communications...",
  "Brewing espresso for tired voltage regulators...",
  "Arranging playdate for lonely rural substations...",
  "Conducting a orchestra of humming transformers...",
  "Teaching squirrels the 'stop, drop, and roll' technique...",
  "Organizing a retirement party for old meters...",
  "Preparing acceptance speeches for reliability awards...",
];

const LoadingScreen = ({ darkMode, onTimeout }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentMessage, setCurrentMessage] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onTimeout) {
        onTimeout("Loading timeout. The application took too long to respond.");
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timeoutId);
  }, [onTimeout]);

  useEffect(() => {
    if (!currentMessage) {
      const randomIndex = Math.floor(Math.random() * loadingMessages.length);
      setCurrentMessage(loadingMessages[randomIndex]);
    }

    let timer;

    if (isTyping) {
      if (displayText !== currentMessage) {
        timer = setTimeout(() => {
          setDisplayText(currentMessage.slice(0, displayText.length + 1));
        }, 50);
      } else {
        timer = setTimeout(() => {
          setIsTyping(false);
        }, 1000);
      }
    } else {
      if (displayText) {
        timer = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 30);
      } else {
        setCurrentMessage("");
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timer);
  }, [displayText, isTyping, currentMessage]);

  return (
    <div
      className={`flex flex-col items-center justify-center h-screen ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="text-center mb-8">
        <img
          alt="GridCritical Logo"
          className="mx-auto h-24 w-auto mb-4"
          src={logo}
        />
        <h2 className="text-3xl font-bold text-orange-600 bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-600">
          GridCritical
        </h2>
        <p
          className={`mt-4 text-xl ${
            darkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          Please stand-by, GridCritical is booting up.
        </p>
      </div>

      <div className="fixed bottom-8 left-8 flex items-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-4 mr-4 ${
            darkMode ? "border-orange-500" : "border-gray-900"
          }`}
          style={{ animationDuration: "2s" }}
        ></div>
        <p className="text-xl font-semibold">
          {displayText}
          <span className="animate-pulse">|</span>
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;
